import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../context/SocketContext';
import { authAPI, analyticsAPI, logsAPI } from '../api/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { isConnected, childStatuses, liveLocation, sosAlerts, riskAlerts } = useSocket();
  const [children, setChildren] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [childRes, logsRes] = await Promise.all([
        authAPI.children(),
        logsAPI.getRecent()
      ]);
      const kids = childRes.data.children;
      setChildren(kids);
      setRecentLogs(logsRes.data.logs);

      // Load summaries for each child
      const summaryResults = {};
      await Promise.all(kids.map(async (kid) => {
        try {
          const res = await analyticsAPI.getSummary(kid._id);
          summaryResults[kid._id] = res.data;
        } catch {}
      }));
      setSummaries(summaryResults);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const totalScreenTime = Object.values(summaries).reduce((sum, s) => sum + (s?.totalScreenTime || 0), 0);
  const onlineCount = children.filter(c => childStatuses[c._id]?.isOnline || c.isOnline).length;

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <div className="card hero-card" style={{ marginBottom: 18 }}>
        <div className="hero-title">Family Safety Command Center</div>
        <div className="hero-subtitle">
          Real-time protection with live location, behavior signals, and remote interventions.
        </div>
        <div className="hero-actions">
          <button className="btn btn-primary" onClick={() => navigate('/dashboard/control')}>🎮 Open Live Control</button>
          <button className="btn btn-outline" onClick={() => navigate('/dashboard/location')}>📍 Track Devices</button>
          <button className="btn btn-outline" onClick={() => navigate('/dashboard/logs')}>📋 Audit Logs</button>
        </div>
      </div>

      {/* Risk Alerts */}
      {riskAlerts.length > 0 && (
        <div style={{
          background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: 12, padding: '12px 16px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 12
        }}>
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--yellow)' }}>Risk Alert</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {riskAlerts[0].alerts[0]?.message}
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">👶</div>
          <div className="stat-info">
            <div className="stat-value">{children.length}</div>
            <div className="stat-label">Linked Devices</div>
            <div className="stat-change up">{onlineCount} online now</div>
          </div>
        </div>
        <div className="stat-card" onClick={() => navigate('/dashboard/sos')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon red">🆘</div>
          <div className="stat-info">
            <div className="stat-value">{sosAlerts.length}</div>
            <div className="stat-label">Active SOS Alerts</div>
            {sosAlerts.length > 0 && <div className="stat-change down">Action required!</div>}
          </div>
        </div>
        <div className="stat-card" onClick={() => navigate('/dashboard/analytics')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon orange">📱</div>
          <div className="stat-info">
            <div className="stat-value">{Math.floor(totalScreenTime / 60)}h {totalScreenTime % 60}m</div>
            <div className="stat-label">Total Screen Time Today</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">🔌</div>
          <div className="stat-info">
            <div className="stat-value">{isConnected ? 'Live' : 'Offline'}</div>
            <div className="stat-label">Server Connection</div>
            <div className={`stat-change ${isConnected ? 'up' : 'down'}`}>
              {isConnected ? 'Real-time active' : 'Reconnecting...'}
            </div>
          </div>
        </div>
      </div>

      {/* Children Cards */}
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>
          👶 Connected Children
        </h3>
        {children.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-icon">📱</div>
              <h3>No Devices Linked</h3>
              <p>Go to Settings to get your pairing code and link the child's Android device.</p>
              <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/dashboard/settings')}>
                ⚙️ Get Pairing Code
              </button>
            </div>
          </div>
        ) : (
          <div className="dashboard-grid">
            {children.map(child => {
              const isOnline = childStatuses[child._id]?.isOnline ?? child.isOnline;
              const loc = liveLocation[child._id];
              const summary = summaries[child._id];
              return (
                <div key={child._id} className="card">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                    <div className="child-avatar" style={{ width: 48, height: 48, fontSize: 20 }}>
                      {child.name[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{child.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{child.email}</div>
                    </div>
                    <span className={`badge ${isOnline ? 'badge-online' : 'badge-offline'}`}>
                      {isOnline ? '● Online' : '○ Offline'}
                    </span>
                  </div>

                  {loc && (
                    <div style={{
                      background: 'var(--bg-secondary)', borderRadius: 10, padding: '10px 12px',
                      fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12
                    }}>
                      📍 {loc.address || `${loc.latitude?.toFixed(4)}, ${loc.longitude?.toFixed(4)}`}
                    </div>
                  )}

                  {summary && (
                    <div style={{
                      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14
                    }}>
                      <div style={{ textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: 10, padding: 10 }}>
                        <div style={{ fontSize: 18, fontWeight: 800 }}>
                          {Math.floor((summary.avgScreenTime || 0) / 60)}h {(summary.avgScreenTime || 0) % 60}m
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Avg/day</div>
                      </div>
                      <div style={{ textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: 10, padding: 10 }}>
                        <div style={{ fontSize: 18, fontWeight: 800 }}>{summary.topApps?.length || 0}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Apps Used</div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-outline btn-sm" style={{ flex: 1 }} onClick={() => navigate('/dashboard/location')}>📍 Track</button>
                    <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => navigate('/dashboard/control')}>🎮 Control</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">📋 Recent Activity</div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/dashboard/logs')}>View All</button>
        </div>
        {recentLogs.length === 0 ? (
          <div className="empty-state" style={{ padding: 24 }}>
            <p>No recent activity</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead><tr>
                <th>Action</th><th>Child</th><th>Status</th><th>Time</th>
              </tr></thead>
              <tbody>
                {recentLogs.slice(0, 8).map((log, i) => (
                  <tr key={i}>
                    <td>
                      <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {log.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </td>
                    <td>{log.childId?.name || '—'}</td>
                    <td>
                      <span className={`badge ${
                        log.status === 'completed' || log.status === 'approved' ? 'badge-online' :
                        log.status === 'denied' ? 'badge-danger' : 'badge-info'
                      }`}>{log.status}</span>
                    </td>
                    <td>{format(new Date(log.timestamp), 'MMM d, HH:mm')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
